'use client';

import { useEffect, useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselControls,
  CarouselNavigation,
  CarouselDots,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import {
  carouselControlsClass,
  carouselControlsUseRuntime,
  carouselRowClass,
  carouselSlideClass,
} from '@/app/_components/shared/partner-list/partner-list.layout';
import type { LogoSize } from '@/app/_components/shared/partner-list/utils/logo-size';
import { getItemTrackId } from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { cn } from '@/app/_lib/utils';
import type { PartnerItem } from '@/entities/models/page';
import { PartnerCard } from './partner-card';

interface PartnerListCarouselProps {
  partners: PartnerItem[];
  showNavigation?: boolean;
  showDots?: boolean;
  autoplay?: boolean;
  logoSize?: LogoSize;
  isNested?: boolean;
  trackingPrefix?: string;
}

// Slide widths, gaps and control visibility all live in partner-list.layout
// (keyed off logoSize / isNested / count). This component only wires embla and
// the runtime scrollability (drag + autoplay + nested-large control visibility).
export function PartnerListCarousel({
  partners,
  showNavigation = true,
  showDots = true,
  autoplay = false,
  logoSize,
  isNested = false,
  trackingPrefix,
}: PartnerListCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const carouselLabel = useLabel('carousel.partners', 'Partners');
  const size = logoSize ?? 'small';
  const count = partners.length;

  // Whether embla can actually scroll. Used to (a) disable drag when everything
  // fits, (b) only autoplay a scrollable carousel (also avoids the embla-autoplay
  // crash when there's nothing to scroll) and (c) toggle nested-large controls.
  const [canScroll, setCanScroll] = useState(false);
  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCanScroll(api.canScrollPrev() || api.canScrollNext());
    };
    update();
    api.on('reInit', update).on('select', update).on('resize', update);
    return () => {
      api.off('reInit', update).off('select', update).off('resize', update);
    };
  }, [api]);

  const controlsVisible = carouselControlsUseRuntime(size, isNested)
    ? canScroll
    : true;

  return (
    <Carousel
      setApi={setApi}
      opts={{
        align: 'start',
        // Loop and autoplay both only make sense when the carousel can scroll.
        loop: autoplay && canScroll,
        watchDrag: (emblaApi) =>
          emblaApi.canScrollPrev() || emblaApi.canScrollNext(),
      }}
      autoplay={autoplay && canScroll}
      ariaLabel={carouselLabel}
    >
      <CarouselContent className={carouselRowClass(size, isNested, count)}>
        {partners.map((partner, index) => (
          <CarouselItem
            key={partner.id}
            className={carouselSlideClass(size, isNested, count)}
          >
            <PartnerCard
              partner={partner}
              logoSize={logoSize}
              trackingPrefix={
                trackingPrefix
                  ? `${trackingPrefix}-card-${getItemTrackId(partner.id, index)}`
                  : undefined
              }
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      {(showNavigation || showDots) && controlsVisible && (
        <div className={cn('mt-6', carouselControlsClass(size, isNested, count))}>
          <CarouselControls>
            {showNavigation && <CarouselNavigation api={api} />}
            {showDots && <CarouselDots api={api} />}
          </CarouselControls>
        </div>
      )}
    </Carousel>
  );
}
